/**
 * BW19 PR-B — MediaRecorder wrapper for voice capture.
 *
 * Picks the best mime type the browser supports (Safari 17+ on iOS supports
 * audio/mp4; Chrome and Firefox prefer audio/webm with opus). Returns a Blob
 * tagged with the actual mime so the Worker uploads with a correct
 * Content-Type and Whisper accepts it.
 */

const PREFERRED_MIMES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
];

export function pickSupportedMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

export type VoiceRecording = {
  blob: Blob;
  durationS: number;
  mime: string;
};

export class VoiceRecorder {
  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private startedAt = 0;
  private settle: ((value: VoiceRecording) => void) | null = null;
  private fail: ((err: Error) => void) | null = null;
  public mime: string = "";

  async start(): Promise<void> {
    if (this.recorder) throw new Error("already_recording");

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("mediarecorder_unsupported");
    }
    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = pickSupportedMime();
    this.mime = mime ?? "audio/webm";
    this.recorder = mime
      ? new MediaRecorder(this.mediaStream, { mimeType: mime })
      : new MediaRecorder(this.mediaStream);

    this.chunks = [];
    this.recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) this.chunks.push(ev.data);
    };
    this.recorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: this.mime });
      const durationS = Math.max(0, (Date.now() - this.startedAt) / 1000);
      this.cleanup();
      this.settle?.({ blob, durationS, mime: this.mime });
    };
    this.recorder.onerror = (ev) => {
      const err = (ev as unknown as { error?: Error }).error ?? new Error("recorder_error");
      this.cleanup();
      this.fail?.(err);
    };

    this.startedAt = Date.now();
    this.recorder.start();
  }

  stop(): Promise<VoiceRecording> {
    if (!this.recorder) return Promise.reject(new Error("not_recording"));
    return new Promise<VoiceRecording>((resolve, reject) => {
      this.settle = resolve;
      this.fail = reject;
      try {
        this.recorder!.stop();
      } catch (err) {
        this.cleanup();
        reject(err as Error);
      }
    });
  }

  cancel(): void {
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop();
      } catch {
        // ignore
      }
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
    this.recorder = null;
    this.settle = null;
    this.fail = null;
  }
}
